import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LandingHeader } from '../components/LandingHeader';
import { LandingFooter } from '../components/LandingFooter';
import { ImageLightbox } from '../components/ImageLightbox';
import { ImageWithSkeleton } from '../components/ImageWithSkeleton';
import { SectionWave } from '../components/SectionWave';
import {
  Accordion as AccordionBase,
  AccordionItem as AccordionItemBase,
  AccordionTrigger as AccordionTriggerBase,
  AccordionContent as AccordionContentBase,
} from '../components/ui/accordion';

// accordion.tsx é `// @ts-nocheck` sem generics no forwardRef, então o TS
// infere props vazias pra quem o usa — cast local pra desbloquear (mesmo
// padrão de outros componentes ui/* no projeto).
const Accordion = AccordionBase as any;
const AccordionItem = AccordionItemBase as any;
const AccordionTrigger = AccordionTriggerBase as any;
const AccordionContent = AccordionContentBase as any;

import { SOLUTIONS } from '../data/solutions';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowLeft, CheckCircle2, Mail, ArrowRight } from 'lucide-react';

// Alpha já embutido na cor (em vez de um wrapper com opacity-*), senão o
// resultado composto fica praticamente invisível.
const dotPatternStyle = {
  backgroundImage: 'radial-gradient(rgba(255,255,255,0.22) 1.5px, transparent 1.5px)',
  backgroundSize: '26px 26px',
};

export const SolutionPage = () => {
  const { slug } = useParams();
  const solution = SOLUTIONS.find((s) => s.slug === slug);
  const { theme } = useTheme();
  const image = theme === 'dark' ? solution?.imageDark : solution?.imageLight;

  if (!solution) {
    return <Navigate to="/" replace />;
  }

  const otherSolutions = SOLUTIONS.filter((s) => s.slug !== solution.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#052e38] via-[#063a46] to-[#0a5f76]">
          <div className="absolute inset-0" style={dotPatternStyle} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay bg-noise" />
          <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/" className="flex w-fit items-center gap-1.5 text-sm font-medium text-white/70 hover:text-white transition-colors mb-6">
                <ArrowLeft size={16} />
                Voltar para a home
              </Link>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-bold uppercase tracking-wider mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0ba5c6]" />
                Soluções
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-5 leading-[1.1] text-white">
                {solution.title}
              </h1>
              <p className="text-lg text-white/80">{solution.tagline}</p>
            </div>
            <div className="drop-shadow-2xl">
              {image ? (
                <ImageLightbox src={image} alt={`Tela de ${solution.title} do Sistema Neurobit`}>
                  <ImageWithSkeleton
                    key={image}
                    src={image}
                    alt={`Tela de ${solution.title} do Sistema Neurobit`}
                    className="w-full h-auto rounded-xl"
                  />
                </ImageLightbox>
              ) : (
                <div className="aspect-[4/3] rounded-2xl border border-white/15 bg-white/5 flex items-center justify-center">
                  <solution.icon className="text-white/50" size={64} />
                </div>
              )}
            </div>
          </div>

          <SectionWave />
        </section>

        {/* Por que usar */}
        <section>
          <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-8">
                Por que usar {solution.title} no Sistema Neurobit
              </h2>
              <ul className="space-y-5">
                {solution.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#0ba5c6] flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-semibold">{b}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border bg-card text-card-foreground shadow p-6">
              <h3 className="font-bold mb-4">Ideal para</h3>
              <ul className="space-y-3 mb-6">
                {solution.idealFor.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0ba5c6] flex-shrink-0 mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="mailto:contato@sistemaneurobit.com.br">
                <Button className="w-full bg-[#052e38] hover:bg-[#0a4552] text-white">
                  Fale conosco sobre {solution.title}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-muted/30">
          <div className="max-w-3xl mx-auto px-6 py-20">
            <div className="mb-10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0ba5c6] mb-2">Dúvidas frequentes</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold">Perguntas sobre {solution.title}</h2>
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border bg-card divide-y">
              {solution.faq.map((item, i) => (
                <AccordionItem key={item.q} value={`item-${i}`} className="border-b-0 px-5">
                  <AccordionTrigger className="text-left font-semibold hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Outras soluções */}
        <section className="border-t border-border">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0ba5c6] mb-6">Outras soluções</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {otherSolutions.map((s) => (
                <Link
                  key={s.slug}
                  to={`/solucoes/${s.slug}`}
                  className="rounded-xl border bg-card text-card-foreground shadow p-6 hover:shadow-md hover:border-[#0ba5c6]/40 transition-all"
                >
                  <s.icon className="h-7 w-7 text-[#0ba5c6] mb-4" />
                  <h3 className="font-bold mb-1.5">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#052e38] via-[#063a46] to-[#0a5f76]">
          <SectionWave flip />
          <div className="absolute inset-0" style={dotPatternStyle} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay bg-noise" />
          <div className="relative max-w-6xl mx-auto px-6 py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Pronto pra transformar {solution.title.toLowerCase()} na sua clínica?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Fala com a gente sobre o seu dia a dia — a primeira conversa é de diagnóstico, sem compromisso.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="mailto:contato@sistemaneurobit.com.br">
                <Button size="lg" className="bg-white text-[#052e38] hover:bg-white/90">
                  <Mail className="mr-2 h-4 w-4" />
                  Fale conosco
                </Button>
              </a>
              <Link to="/#recursos">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white">
                  Conheça outras soluções
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
};
